import * as React from 'react';

import {
  enumQuoteStatus,
  enumQuoteType,
} from '@rematter/frontend-lib-api-client';
import { Button, ConfirmPopoverButton, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';
import { useLocation } from 'wouter';

import * as state from '../../_state/quoteDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { brokerageRoutes } from '../../index';

import { useQuoteDetailMenuItems } from './hooks/useQuoteDetailMenuItems';

//
// ⚛️ ⚛️ ⚛️ QUOTE DETAIL HEADER ACTIONS ⚛️ ⚛️ ⚛️
//
export const QuoteDetailHeaderActions = () => {
  const t = useTranslations();
  const [, navigate] = useLocation();

  // Atoms
  const { data: quote, refetch } = useAtomValue(state.$remoteQuoteDetail);
  const sendQuote = useSetAtom(state.$sendQuote);
  const rejectQuote = useSetAtom(state.$rejectQuote);
  const acceptQuote = useSetAtom(state.$acceptQuote);
  const convertQuote = useSetAtom(state.$convertQuote);
  const voidQuote = useSetAtom(state.$voidQuote);
  const restoreQuote = useSetAtom(state.$restoreQuote);
  const revertQuoteToOpen = useSetAtom(state.$revertToOpen);

  const toast = useToast();

  // Callbacks
  const handleVoid = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await voidQuote({ id: quote.id });
      toast.success(t.quotes.toast.voided);
    } catch (err) {
      toast.error(
        t.quotes.toast.voidError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, voidQuote, toast, t]);

  const handleSend = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await sendQuote({ id: quote.id });
      toast.success(t.quotes.toast.sent);
    } catch (err) {
      toast.error(
        t.quotes.toast.sendError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, sendQuote, toast, t]);

  const handleReject = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await rejectQuote({ id: quote.id });
      toast.success(t.quotes.toast.rejected);
    } catch (err) {
      toast.error(
        t.quotes.toast.rejectError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, rejectQuote, toast, t]);

  const handleAccept = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await acceptQuote({ id: quote.id });
      toast.success(t.quotes.toast.accepted);
    } catch (err) {
      toast.error(
        t.quotes.toast.acceptError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, acceptQuote, toast, t]);

  const handleRestore = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await restoreQuote({ id: quote.id });
      toast.success(t.quotes.toast.restored);
    } catch (err) {
      toast.error(
        t.quotes.toast.restoreError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, restoreQuote, toast, t]);

  const handleRevertToOpen = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await revertQuoteToOpen({ id: quote.id });
      toast.success(t.quotes.toast.reverted);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [quote?.id, revertQuoteToOpen, toast, t]);

  const handleConvert = React.useCallback(async () => {
    if (!quote?.id) return;
    try {
      await convertQuote({ id: quote.id });
      toast.success(t.quotes.toast.converted);
      refetch?.();
    } catch (err) {
      toast.error(
        t.quotes.toast.convertError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [quote?.id, convertQuote, toast, t, refetch]);

  const handleViewOrder = React.useCallback(() => {
    if (!quote?.convertedToOrderId) return;
    navigate(
      brokerageRoutes.orderDetail.path.replace(':id', quote.convertedToOrderId),
    );
  }, [quote?.convertedToOrderId, navigate]);

  // Other data hooks
  const menuItems = useQuoteDetailMenuItems({
    quote: quote ?? null,
    onVoid: handleVoid,
  });

  if (!quote) return null;

  return (
    <div className="@QuoteDetailHeaderActions flex items-center gap-2">
      <Menu
        trigger={
          <Button
            icon={<LucideMoreHorizontal className="size-4 w-8" />}
            variant="outline"
            className="w-8"
          />
        }
        items={menuItems}
      />
      {quote.status !== enumQuoteStatus.Converted &&
        quote.status !== enumQuoteStatus.Voided && (
          <ConfirmPopoverButton
            onConfirm={handleVoid}
            message={t.quotes.voidDialog.message(quote.quoteNumber)}
            confirmLabel={t.quotes.voidDialog.confirm}
            cancelLabel={t.quotes.voidDialog.cancel}
            buttonProps={{ variant: 'outline', color: 'failure' }}
          >
            {t.quotes.detail.actions.void}
          </ConfirmPopoverButton>
        )}
      {quote.status === enumQuoteStatus.Voided && (
        <Button variant="solid" color="primary" onPress={handleRestore}>
          {t.quotes.detail.actions.restore}
        </Button>
      )}
      {quote.status === enumQuoteStatus.Draft && (
        <Button variant="solid" color="primary" onPress={handleSend}>
          {t.quotes.detail.actions.markInReview}
        </Button>
      )}
      {quote.status === enumQuoteStatus.Sent && (
        <>
          <Button variant="outline" color="failure" onPress={handleReject}>
            {t.quotes.detail.actions.markRejected}
          </Button>
          <Button variant="solid" color="primary" onPress={handleAccept}>
            {t.quotes.detail.actions.markApproved}
          </Button>
        </>
      )}
      {quote.status === enumQuoteStatus.Accepted && (
        <ConfirmPopoverButton
          onConfirm={handleConvert}
          title={t.quotes.convert.approveTitle}
          message={t.quotes.convert.approveMessage(
            quote.type === enumQuoteType.Sales
              ? t.quotes.convert.orderTypes.salesOrder
              : t.quotes.convert.orderTypes.purchaseOrder,
          )}
          confirmLabel={t.quotes.convert.approveConfirm(
            quote.type === enumQuoteType.Sales
              ? t.quotes.convert.orderTypes.salesOrderShort
              : t.quotes.convert.orderTypes.purchaseOrderShort,
          )}
          cancelLabel={t.quotes.convert.approveCancel}
          color="success"
          renderTrigger={(props) => (
            <Button {...props} variant="solid" color="primary">
              {quote.type === enumQuoteType.Sales
                ? t.quotes.detail.actions.createSalesOrder
                : t.quotes.detail.actions.createPurchaseOrder}
            </Button>
          )}
        />
      )}
      {quote.status === enumQuoteStatus.Rejected && (
        <Button variant="solid" color="primary" onPress={handleRevertToOpen}>
          {t.quotes.detail.actions.revertToOpen}
        </Button>
      )}
      {quote.status === enumQuoteStatus.Converted &&
        quote.convertedToOrderId && (
          <Button variant="solid" color="primary" onPress={handleViewOrder}>
            {t.quotes.detail.actions.viewSalesOrder}
          </Button>
        )}
    </div>
  );
};
